import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import shared from './GarageContent.module.scss';
import classes from './BillingPanel.module.scss';
import useFocusedCard from './useFocusedCard';
import {
    getMyDealerInvoicePdf,
    openPdf,
    carFromSnapshot,
    formatUsd,
    formatDay,
    PAYMENT_METHOD_LABELS,
    DEPOSIT_TYPE_LABELS,
    INVOICE_KIND_LABELS,
} from '../../services/dealerGarage';
import { buildVehicleTitle } from '../../helper/vehicleTitle';

// Ledger rows shown before "Show all".
const LEDGER_PREVIEW = 5;

const INVOICE_STATES = {
    paid: { label: 'Paid', tone: shared.toneSuccess },
    due: { label: 'Due', tone: shared.toneWarning },
    overdue: { label: 'Overdue', tone: shared.toneDanger },
    void: { label: 'Void', tone: shared.toneMuted },
};

// Today as YYYY-MM-DD in the member's own timezone, to compare with due dates.
const todayYmd = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const invoiceState = (invoice) => {
    if (invoice.status === 'void') return 'void';
    if (invoice.status === 'paid') return 'paid';
    return invoice.dueDate && invoice.dueDate < todayYmd() ? 'overdue' : 'due';
};

const invoiceRef = (invoice) => (invoice.source === 'quickbooks'
    ? `QuickBooks #${invoice.externalRef}`
    : invoice.number || 'Invoice');

// Deposit methods include a few the invoice payment list does not (Shopify).
const methodLabel = (method) => {
    if (!method) return '';
    return PAYMENT_METHOD_LABELS[method] || (method.charAt(0).toUpperCase() + method.slice(1));
};

const signedUsd = (amount) => (amount > 0 ? `+${formatUsd(amount)}` : formatUsd(amount));

/**
 * The deposit held on the account, its most recent movements, and how a
 * deposit works (the API's own `policy` lines, only sent to a customer who
 * has or had one).
 */
const DepositCard = ({ deposit }) => {
    const [showAll, setShowAll] = useState(false);
    const rows = Array.isArray(deposit.transactions) ? deposit.transactions : [];
    const visible = showAll ? rows : rows.slice(0, LEDGER_PREVIEW);
    const policy = Array.isArray(deposit.policy) ? deposit.policy.filter(Boolean) : [];

    return (
        <section className={`${shared.card} ${classes.deposit}`} aria-labelledby="garage-deposit-title">
            <div className={classes.depositHead}>
                <h2 id="garage-deposit-title" className={classes.panelTitle}>Deposit balance</h2>
                <strong className={classes.depositAmount}>{formatUsd(deposit.balance || 0)}</strong>
            </div>
            {rows.length === 0 ? (
                <p className={classes.muted}>No deposit activity yet.</p>
            ) : (
                <>
                    <ul className={classes.ledger}>
                        {visible.map((row) => (
                            <li key={row._id}>
                                <div className={classes.ledgerMain}>
                                    <strong>{DEPOSIT_TYPE_LABELS[row.type] || row.type}</strong>
                                    <span>
                                        {[formatDay(row.date || row.createdAt), methodLabel(row.method), row.reference]
                                            .filter(Boolean).join(' · ')}
                                    </span>
                                </div>
                                <div className={classes.ledgerAmount}>
                                    <strong className={row.amount > 0 ? classes.credit : ''}>{signedUsd(row.amount)}</strong>
                                    <span>Balance {formatUsd(row.balanceAfter)}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                    {rows.length > LEDGER_PREVIEW && (
                        <button type="button" className={classes.linkButton} onClick={() => setShowAll((all) => !all)}>
                            {showAll ? 'Show recent only' : `Show all ${rows.length} entries`}
                        </button>
                    )}
                </>
            )}
            {policy.length > 0 && (
                <div className={classes.policy}>
                    <h3 className={classes.policyTitle}>How your deposit works</h3>
                    <ul>
                        {policy.map((line) => <li key={line}>{line}</li>)}
                    </ul>
                </div>
            )}
        </section>
    );
};

/**
 * One invoice. Ours (source 'site') come with a PDF carrying the wire / ACH
 * details; QuickBooks ones are a record of an invoice issued there, sometimes
 * with an online pay link.
 */
const InvoiceCard = ({ invoice, focused }) => {
    const cardRef = useFocusedCard(focused);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState('');

    const state = invoiceState(invoice);
    const open = state === 'due' || state === 'overdue';
    const car = invoice.vehicle ? carFromSnapshot(invoice) : null;
    let carName = '';
    if (car) carName = (car.make || car.model) ? buildVehicleTitle(car) : (car.title || '');
    const payments = Array.isArray(invoice.payments) ? invoice.payments : [];
    // The member's deposit put toward this invoice reads as a credit against
    // the total, not as money they sent.
    const depositApplied = payments.filter((payment) => payment.method === 'deposit');
    const received = payments.filter((payment) => payment.method !== 'deposit');
    // The API only stores https pay links; checked again so nothing else is
    // ever handed to an href.
    const payUrl = /^https:\/\//i.test(invoice.payUrl || '') ? invoice.payUrl : '';

    // openPdf opens the tab synchronously, so this must run straight from the
    // click - no await before it.
    const viewPdf = async () => {
        setPdfError('');
        setPdfLoading(true);
        try {
            await openPdf(() => getMyDealerInvoicePdf(invoice._id));
        } catch (err) {
            setPdfError('The invoice PDF could not be opened. Please try again.');
        } finally {
            setPdfLoading(false);
        }
    };

    return (
        <article
            ref={cardRef}
            className={`${shared.card} ${focused ? shared.cardFocused : ''} ${state === 'void' ? classes.voided : ''}`}
        >
            <div className={shared.cardHead}>
                <div>
                    <span className={shared.typeTag}>{INVOICE_KIND_LABELS[invoice.kind] || 'Other'}</span>
                    <strong className={classes.ref}>{invoiceRef(invoice)}</strong>
                </div>
                <span className={`${shared.status} ${INVOICE_STATES[state].tone}`}>{INVOICE_STATES[state].label}</span>
            </div>

            {(carName || invoice.purchaseId || invoice.inspectionId) && (
                <p className={classes.forLine}>
                    {carName && (
                        <span>
                            <i className="fas fa-car" aria-hidden="true"></i> {carName}
                            {invoice.vin ? ` · VIN ${invoice.vin}` : ''}
                        </span>
                    )}
                    {invoice.purchaseId && (
                        <Link to={`/garage?tab=purchases&purchase=${invoice.purchaseId}`}>View purchase</Link>
                    )}
                    {!invoice.purchaseId && invoice.inspectionId && (
                        <Link to={`/garage?tab=inspections&inspection=${invoice.inspectionId}`}>View inspection</Link>
                    )}
                </p>
            )}

            <dl className={`${shared.meta} ${classes.amounts}`}>
                {invoice.issueDate && <div><dt>Issued</dt><dd>{formatDay(invoice.issueDate)}</dd></div>}
                {invoice.dueDate && (
                    <div>
                        <dt>Due</dt>
                        <dd className={state === 'overdue' ? classes.overdue : ''}>{formatDay(invoice.dueDate)}</dd>
                    </div>
                )}
                <div><dt>Total</dt><dd>{formatUsd(invoice.total || 0)}</dd></div>
                <div><dt>Paid</dt><dd>{formatUsd(invoice.amountPaid || 0)}</dd></div>
                {state !== 'void' && (
                    <div><dt>Balance</dt><dd className={classes.balance}>{formatUsd(invoice.balance || 0)}</dd></div>
                )}
            </dl>

            {depositApplied.length > 0 && (
                <ul className={`${classes.payments} ${classes.credits}`}>
                    {depositApplied.map((payment) => (
                        <li key={payment._id}>
                            <span>
                                Less: deposit applied
                                {payment.receivedAt && <span className={classes.creditDate}> · {formatDay(payment.receivedAt)}</span>}
                            </span>
                            <strong>−{formatUsd(payment.amount)}</strong>
                        </li>
                    ))}
                </ul>
            )}

            {received.length > 0 && (
                <>
                    <h3 className={shared.subhead}>Payments received</h3>
                    <ul className={classes.payments}>
                        {received.map((payment) => (
                            <li key={payment._id}>
                                <span>
                                    {[formatDay(payment.receivedAt), methodLabel(payment.method), payment.reference]
                                        .filter(Boolean).join(' · ')}
                                </span>
                                <strong>{formatUsd(payment.amount)}</strong>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {invoice.notes && <p className={shared.message}>{invoice.notes}</p>}

            {(invoice.hasPdf || (open && payUrl)) && (
                <div className={classes.actions}>
                    {invoice.hasPdf && (
                        <button type="button" className={shared.actionButton} onClick={viewPdf} disabled={pdfLoading}>
                            <i className="fas fa-file-pdf" aria-hidden="true"></i>
                            {pdfLoading ? 'Opening…' : 'View PDF'}
                        </button>
                    )}
                    {open && payUrl && (
                        <a
                            href={payUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${shared.actionButton} ${shared.actionPrimary}`}
                        >
                            <i className="fas fa-credit-card" aria-hidden="true"></i>
                            Pay online
                        </a>
                    )}
                </div>
            )}
            {pdfError && <p className={shared.inlineError} role="alert">{pdfError}</p>}

            {open && invoice.source === 'site' && (
                <p className={classes.payHint}>
                    <i className="fas fa-info-circle" aria-hidden="true"></i>
                    Pay by wire or ACH using the details on the PDF, with the invoice number as the reference.
                </p>
            )}
        </article>
    );
};

/**
 * The Billing tab: deposit balance, the member's details, then every invoice
 * newest first. `deposit` is only passed for a member who has one;
 * `details` is the "Your details" card. `focusId` is the invoice a deep link
 * (?invoice=<id>) points at.
 */
const BillingPanel = ({ invoices, deposit, details, focusId }) => {
    const outstanding = invoices
        .filter((invoice) => invoice.status === 'sent')
        .reduce((sum, invoice) => sum + (Number(invoice.balance) || 0), 0);

    return (
        <div className={classes.panel}>
            {deposit && <DepositCard deposit={deposit} />}
            {details}

            <div className={classes.invoicesHead}>
                <h2 className={classes.panelTitle}>Invoices</h2>
                {outstanding > 0 && (
                    <span className={classes.outstanding}>Outstanding {formatUsd(outstanding)}</span>
                )}
            </div>
            {invoices.length === 0 ? (
                <p className={classes.muted}>No invoices yet.</p>
            ) : (
                <div className={shared.list}>
                    {invoices.map((invoice) => (
                        <InvoiceCard key={invoice._id} invoice={invoice} focused={invoice._id === focusId} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default BillingPanel;
